import { LinearTransform } from '@st/geom'
import type { Point } from '@st/geom'
import { useCallback, useRef } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { useLinearTransform } from './use-linear-transform'

export type MouseState = 'down' | 'up'
export type PointerMoveEvent = {
  point: Point
  originalEvent: ReactPointerEvent
  mouseState: MouseState
}

type Opts = {
  canvasClass?: string
  onPointerMove: (e: PointerMoveEvent) => void
}

export function usePointerMove({ onPointerMove, canvasClass }: Opts) {
  const ref = useRef<HTMLDivElement | null>(null)
  const lastEventRef = useRef<ReactPointerEvent | null>(null)
  const rafIdRef = useRef<number | null>(null)
  const transform = useLinearTransform()

  const flush = useCallback(() => {
    rafIdRef.current = null
    if (lastEventRef.current) {
      const base = toBasePointerEvent(lastEventRef.current, transform, canvasClass, ref.current)
      onPointerMove({ ...base, mouseState: lastEventRef.current.buttons ? 'down' : 'up' })
    }
  }, [onPointerMove, transform])

  const schedule = useCallback(
    (e: ReactPointerEvent) => {
      lastEventRef.current = e
      if (rafIdRef.current == null) {
        rafIdRef.current = requestAnimationFrame(flush)
      }
    },
    [flush]
  )

  const onPointerMoveHandler = useCallback(
    (e: ReactPointerEvent) => {
      schedule(e)
    },
    [schedule]
  )

  return { ref, onPointerMove: onPointerMoveHandler }
}

function toBasePointerEvent(
  e: ReactPointerEvent,
  transform: LinearTransform,
  canvasClass: string | undefined,
  element: HTMLElement | null
): Omit<PointerMoveEvent, 'mouseState'> {
  const target = e.target as HTMLElement
  const parent = canvasClass
    ? target.classList.contains(canvasClass)
      ? target
      : target.closest(`.${canvasClass}`)
    : element
  const rect = (parent ?? target).getBoundingClientRect()
  const rawPoint = { x: e.clientX - rect.left, y: e.clientY - rect.top }
  const inverted = LinearTransform.invert(transform)
  const point = LinearTransform.apply(rawPoint, inverted)
  return { point, originalEvent: e }
}
